<template>
  <LoadingSpinner :loading='loading'> </LoadingSpinner>

  <div class="container-fluid mt-4 mb-4" style="max-width:1860px; margin:0 auto">
    <h3 class="mb-4">SwedbankPay Transactions</h3>

    <div v-if="hasMultiLocations()" class="select-location-box mt-4 mb-3">
      <span class="ingredient-title">For avdeling</span>
      <select class="form-select" v-model="locationId" @change="loadTransactions(); loadCompleteUrl()">
        <option v-for="(location, index) in locations" :key="index" :value="location.id">{{ location.name }}</option>
      </select>
    </div>

    <div v-if="transactions.length === 0 && !loading" class="text-muted mt-4">
      Ingen transaksjoner funnet.
    </div>

    <div class="tx-grid">
      <div v-for="(tx, index) in transactions" :key="index">
        <div class="tx-card" :class="tx.orderCreated ? 'border-status-created' : 'border-status-not-created'">
          <!-- Header -->
          <div class="tx-card-header">
            <div>
              <div class="tx-title">
                <span class="tx-id">{{ tx.orderReference }}</span>
              </div>
              <div class="tx-time">{{ formatDate(tx.createdDateTime) }}</div>
            </div>
          </div>

          <div class="tx-card-divider"></div>

          <!-- Body -->
          <div class="tx-card-body">
            <div class="tx-info-row">
              <span class="tx-label">payeeReference</span>
              <span class="tx-value">{{ tx.payeeReference }}</span>
            </div>
            <div class="tx-info-row">
              <span class="tx-label">orderReference</span>
              <span class="tx-value">{{ tx.orderReference }}</span>
            </div>

            <div v-if="getPaymentType(tx)" class="tx-info-row">
              <span class="tx-label">Betalingsmetode</span>
              <span class="tx-value">{{ getPaymentType(tx) }}</span>
            </div>
            <div v-if="tx.order && tx.order.channel" class="tx-info-row">
              <span class="tx-label">Kanal</span>
              <span class="tx-value">{{ tx.order.channel }}</span>
            </div>
            <div v-if="tx.order && tx.order.totalAmount !== undefined" class="tx-info-row">
              <span class="tx-label">Totalbeløp</span>
              <span class="tx-value tx-amount">{{ formatAmount(tx.order.totalAmount) }}</span>
            </div>

            <div class="tx-badges">
              <span v-if="tx.orderCreated" class="tx-badge badge-created">Ordre opprettet</span>
              <span v-else class="tx-badge badge-not-created">Ordre ikke opprettet</span>
            </div>
          </div>

          <!-- Footer -->
          <div v-if="!tx.orderCreated" class="tx-card-footer">
            <div class="d-flex gap-2">
              <button class="bo_btn flex-fill" @click="openPaymentStatus(tx)">
                Storefront Create Order
              </button>
              <button class="bo_btn flex-fill" @click="showOrderDialog(tx)">
                Manual Create Order
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Order detail dialog -->
  <popup-modal ref="orderModal">
    <div v-if="selectedTx">
      <div class="d-flex justify-content-between align-items-center">
        <h3 class="mb-0">Order Data</h3>
        <button class="bo_btn" @click="showJsonDialog()">JSON</button>
      </div>

      <div class="modal-order-data mt-3">
        <div class="modal-row">
          <span class="modal-label">orderReference</span>
          <span class="modal-value">{{ selectedTx.orderReference }}</span>
        </div>
        <div class="modal-row">
          <span class="modal-label">payeeReference</span>
          <span class="modal-value">{{ selectedTx.payeeReference }}</span>
        </div>
        <div class="modal-row">
          <span class="modal-label">createdDateTime</span>
          <span class="modal-value">{{ formatDate(selectedTx.createdDateTime) }}</span>
        </div>

        <div v-if="selectedTx.order" class="mt-3">
          <h6>Order</h6>
          <div class="modal-row" v-if="selectedTx.order.orderId">
            <span class="modal-label">orderId</span>
            <span class="modal-value">{{ selectedTx.order.orderId }}</span>
          </div>
          <div class="modal-row" v-if="selectedTx.order.name">
            <span class="modal-label">Navn</span>
            <span class="modal-value">{{ selectedTx.order.name }}</span>
          </div>
          <div class="modal-row" v-if="selectedTx.order.mobileNumber">
            <span class="modal-label">Telefon</span>
            <span class="modal-value">{{ selectedTx.order.mobileNumber }}</span>
          </div>
          <div class="modal-row" v-if="selectedTx.order.orderType">
            <span class="modal-label">Type</span>
            <span class="modal-value">{{ selectedTx.order.orderType }}</span>
          </div>
          <div class="modal-row" v-if="getPaymentType(selectedTx)">
            <span class="modal-label">Betalingsmetode</span>
            <span class="modal-value">{{ getPaymentType(selectedTx) }}</span>
          </div>
          <div class="modal-row" v-if="selectedTx.order.channel">
            <span class="modal-label">Kanal</span>
            <span class="modal-value">{{ selectedTx.order.channel }}</span>
          </div>
          <div class="modal-row" v-if="selectedTx.order.totalAmount !== undefined">
            <span class="modal-label">Totalbeløp</span>
            <span class="modal-value">{{ formatAmount(selectedTx.order.totalAmount) }}</span>
          </div>
          <div v-if="selectedTx.order.cartItems && selectedTx.order.cartItems.length" class="mt-2">
            <span class="modal-label">Varer</span>
            <ul class="modal-cart-items mt-1">
              <li v-for="(item, i) in selectedTx.order.cartItems" :key="i">
                {{ item.count }}x {{ item.itemName }} <span v-if="item.price" class="text-muted">- {{ item.price }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="modal-btn-panel mt-4">
        <button class="bo_btn btn-width" @click="closeOrderDialog()">Avbryt</button>
        <button class="bo_btn bo_btn_green btn-width ms-3" :disabled="creating" @click="confirmCreateOrder()">
          {{ creating ? 'Creating...' : 'Create Order' }}
        </button>
      </div>
    </div>
  </popup-modal>

  <!-- JSON detail dialog -->
  <popup-modal ref="jsonModal">
    <div>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h3 class="mb-0">JSON</h3>
        <button class="btn-close" @click="closeJsonDialog()"></button>
      </div>
      <div class="json-content">
        <JsonViewer :value="jsonData" :expand-depth="Infinity" copyable sort class="no-root-toggle" />
      </div>
    </div>
  </popup-modal>
</template>

<script>
  import authHeader from "@/services/auth/AuthHeader";
  import axios from "axios";
  import LoadingSpinner from "@/components/other/LoadingSpinner";
  import PopupModal from "@/components/other/PopupModal";
  import LocationUtil from "@/util/LocationUtil";
  import LocationService from "@/services/location/LocationService";
  import MathUtil from "@/util/MathUtil";
  import SettingsService from "@/services/settings/SettingsService";
  import { JsonViewer } from "vue3-json-viewer";
  import "vue3-json-viewer/dist/vue3-json-viewer.css";

  export default {
    components: {LoadingSpinner, PopupModal, JsonViewer},
    data() {
      return {
        loading: true,
        locationId: '',
        locations: [],
        transactions: [],
        selectedTx: null,
        creating: false,
        jsonData: {},
        completeUrl: ''
      };
    },
    mounted() {
      LocationService.listLocations().then(
          (response) => {
            this.locations = response.data;
            this.locationId = this.locations.length ? this.locations[0].id : '';
            this.loadTransactions();
            this.loadCompleteUrl();
          },
          (error) => {
            console.log(error);
          }
      );
    },
    methods: {
      loadCompleteUrl(){
        SettingsService.getSwedbankPayAccount(this.locationId).then(
            (response) => {
              if(response.data && response.data.completeUrl){
                this.completeUrl = response.data.completeUrl;
              }
            },
            (error) => {
              console.log(error);
            }
        );
      },
      hasMultiLocations(){
        return LocationUtil.hasMultiLocations();
      },
      loadTransactions(){
        this.loading = true;
        let user = JSON.parse(localStorage.getItem('user'));
        let url = '/api/payment/getSwedbankPayTransactions?merchant=' + user.merchant + '&locationId=' + this.locationId;
        let basicAuth = authHeader();
        axios.get(url, { headers: basicAuth }).then(
            (response) => {
              this.transactions = response.data.sort((a, b) => {
                if (a.orderCreated !== b.orderCreated) return a.orderCreated ? 1 : -1;
                return new Date(b.createdDateTime) - new Date(a.createdDateTime);
              });
              this.loading = false;
            },
            (error) => {
              console.log(error);
              this.loading = false;
            }
        );
      },
      getPaymentStatus(tx){
        tx.checkingStatus = true;
        let user = JSON.parse(localStorage.getItem('user'));
        let url = '/api/storefront/swedbankPayGetPaymentStatus?merchant=' + user.merchant
            + '&locationId=' + this.locationId
            + '&payeeReference=' + tx.payeeReference
            + '&orderReference=' + tx.orderReference;
        let basicAuth = authHeader();
        axios.get(url, { headers: basicAuth }).then(
            (response) => {
              tx.checkingStatus = false;
              this.loadTransactions();
            },
            (error) => {
              console.log(error);
              tx.checkingStatus = false;
            }
        );
      },
      openPaymentStatus(tx){
        let url = this.completeUrl + '?payeeReference=' + tx.payeeReference + '&orderReference=' + tx.orderReference;
        window.open(url, '_blank');
      },
      showOrderDialog(tx){
        this.selectedTx = tx;
        this.creating = false;
        this.$refs.orderModal.open();
      },
      showJsonDialog(){
        this.jsonData = this.selectedTx.order || {};
        this.$refs.jsonModal.open();
      },
      closeJsonDialog(){
        this.$refs.jsonModal.close();
      },
      closeOrderDialog(){
        this.$refs.orderModal.close();
        this.selectedTx = null;
      },
      confirmCreateOrder(){
        this.creating = true;
        let user = JSON.parse(localStorage.getItem('user'));
        let url = '/api/payment/createOrderFromSwedbankPayTransaction?merchant=' + user.merchant
            + '&payeeReference=' + this.selectedTx.payeeReference
            + '&orderReference=' + this.selectedTx.orderReference;
        let basicAuth = authHeader();
        axios.post(url, null, { headers: basicAuth }).then(
            (response) => {
              this.creating = false;
              this.closeOrderDialog();
              this.loadTransactions();
            },
            (error) => {
              console.log(error);
              this.creating = false;
            }
        );
      },
      getPaymentType(tx){
        if(tx.order && tx.order.payments && tx.order.payments.length > 0){
          return tx.order.payments[0].paymentType || tx.order.payments[0].onlinePaymentProvider || null;
        }
        return null;
      },
      formatAmount(amount){
        return MathUtil.displayTwoDecimals(amount);
      },
      formatDate(dateStr){
        if(!dateStr) return '';
        let date = new Date(dateStr);
        let day = String(date.getDate()).padStart(2, '0');
        let month = String(date.getMonth() + 1).padStart(2, '0');
        let year = date.getFullYear();
        let hours = String(date.getHours()).padStart(2, '0');
        let minutes = String(date.getMinutes()).padStart(2, '0');
        return day + '.' + month + '.' + year + ' kl. ' + hours + ':' + minutes;
      }
    }
  };
</script>

<style scoped>
.tx-grid {
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 12px;
}

@media (min-width: 576px) {
  .tx-grid { grid-template-columns: repeat(2, 1fr); }
}

@media (min-width: 992px) {
  .tx-grid { grid-template-columns: repeat(3, 1fr); }
}

@media (min-width: 1400px) {
  .tx-grid { grid-template-columns: repeat(4, 1fr); }
}

.tx-card {
  background: var(--bo-surface);
  border-radius: 10px;
  border: 1px solid var(--bo-line);
  border-left: 4px solid var(--bo-muted-2);
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  overflow: hidden;
}

.border-status-created { border-left-color: #22c55e; }
.border-status-not-created { border-left-color: #f59e0b; }

.tx-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 14px 16px 10px;
}

.tx-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tx-id {
  font-weight: 600;
  font-size: 15px;
  color: var(--bo-ink);
}

.tx-time {
  font-size: 13px;
  color: var(--bo-ink);
  margin-top: 4px;
}

.tx-card-divider {
  height: 1px;
  background: var(--bo-surface-2);
  margin: 0 16px;
}

.tx-card-body {
  padding: 10px 16px;
}

.tx-info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 3px 0;
}

.tx-label {
  font-size: 13px;
  color: var(--bo-muted-2);
}

.tx-value {
  font-size: 14px;
  color: var(--bo-ink);
  text-align: right;
  word-break: break-all;
}

.tx-badges {
  display: flex;
  gap: 6px;
  margin-top: 8px;
  flex-wrap: wrap;
}

.tx-badge {
  font-size: 12px;
  font-weight: 500;
  padding: 3px 10px;
  border-radius: 12px;
}

.tx-amount {
  font-size: 18px;
  font-weight: 700;
}

.badge-created { background: var(--bo-ok-bg); color: var(--bo-ok-fg); }
.badge-not-created { background: var(--bo-warn-bg); color: var(--bo-warn-fg); }

.json-content {
  max-height: 500px;
  overflow-y: auto;
  background: var(--bo-surface-2);
  border-radius: 6px;
  padding: 12px;
}

.no-root-toggle :deep(.jv-code > .jv-node > .jv-toggle) {
  display: none !important;
}

.no-root-toggle :deep(.jv-code > .jv-node > .jv-toggle + .jv-key + .jv-push > .jv-item) {
  margin-left: 0 !important;
}

.json-content pre {
  margin: 0;
  font-size: 13px;
  white-space: pre-wrap;
  word-break: break-all;
}

.tx-card-footer {
  padding: 10px 16px;
  background: var(--bo-surface-2);
  border-top: 1px solid var(--bo-surface-2);
}

/* Modal styles */
.modal-order-data {
  max-height: 400px;
  overflow-y: auto;
}

.modal-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  border-bottom: 1px solid var(--bo-surface-2);
  gap: 16px;
}

.modal-label {
  font-size: 13px;
  color: var(--bo-muted-2);
}

.modal-value {
  font-weight: 600;
  font-size: 14px;
  color: var(--bo-ink);
  text-align: right;
  word-break: break-all;
}

.modal-value-small {
  font-size: 12px;
  max-width: 250px;
}

.modal-cart-items {
  list-style: none;
  padding-left: 0;
  font-size: 14px;
}

.modal-cart-items li {
  padding: 2px 0;
  border-bottom: 1px solid var(--bo-surface-2);
}

.modal-btn-panel {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  border-top: var(--bo-muted-2) solid 1px;
  padding-top: 15px;
}

.btn-width {
  width: 120px;
  text-align: center;
}

.bo_btn_green {
  background-color: #22c55e !important;
  border-color: #22c55e !important;
  color: #fff !important;
}
</style>
